import React, { useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';

import {
  getContractBenefits,
  getFees,
  getFeesIncentives,
  getIncentives,
  getLivingInfo,
  getOccupancies,
} from 'apis/CommunityAPI';
import { WIDGET_TYPES } from 'common/Widgets/constants';
import { DATA_RANGE_TYPE } from 'common/Widgets/Widget/constants';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import useDataQuarterSelection from 'hooks/useDataQuarterSelection';
import { useWidgetDataFetcher } from 'hooks/useWidgetDataFetcher';
import { useAppSelector } from 'store/hook';
import { CommunityType } from 'types/communities';
import { mapCommunityLivingInfoToData } from 'utils/data/accommodations';
import { mapCareFeeResponseToData, mapCommunityFeeResponseToData } from 'utils/data/fees';
import { mapCommunityOccupancyResponseToData } from 'utils/data/occupancies';
import { getIncentiveQuarterDateRange, getQuarterEndDate } from 'utils/dateQuarter';
import { getById } from 'utils/helpers/idMap';

import { CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_OPTIONS } from './constants';
import { CMASummaryFilters } from './types';
import { getCMASummaryData } from './utils';
import { usePublicationContext } from '../Publication/PublicationContext';

const buildSnapshotRequest = <T>(snapshotUuid: string | null, task: (uuid: string) => Promise<T>, fallback: T) =>
  snapshotUuid ? task(snapshotUuid) : Promise.resolve(fallback);

const useFetchCMASummaryData = (community: any, competitors: any[], filters: CMASummaryFilters) => {
  const { publication } = usePublicationContext();
  const { fetchWidgetData, showContractLabelAnnotations } = useWidgetDataFetcher();
  const { viewFilters } = useCommunityViewContext();
  const {
    snapshotsByCommunity,
    snapshotMetaByCommunity,
    hasSnapshotOverrides,
    selection: dataQuarterSelection,
    isLatestQuarterSelected,
    quarters,
    snapshotUuids,
  } = useDataQuarterSelection();
  const selectedQuarterCutoffDate = useMemo(
    () => (isLatestQuarterSelected ? null : getQuarterEndDate(dataQuarterSelection?.selectedQuarterSlug ?? null)),
    [dataQuarterSelection?.selectedQuarterSlug, isLatestQuarterSelected]
  );

  const effectiveCompetitors = useMemo(() => {
    if (hasSnapshotOverrides) {
      return competitors || [];
    }

    return (competitors ?? []).filter((competitor) => Boolean(getById(snapshotsByCommunity, competitor.id)));
  }, [competitors, snapshotsByCommunity, hasSnapshotOverrides]);

  const communitySnapshotUuid = getById(snapshotsByCommunity, community?.id);
  const allowQueries = Boolean(community) && snapshotUuids !== null;

  const {
    data: ratings,
    isLoading: ratingsLoading,
    isFetched: isRatingsFetched,
  } = useQuery({
    queryKey: [
      'CMASummary',
      'ratings',
      community?.id,
      filters?.reviewSources,
      competitors,
      showContractLabelAnnotations,
      selectedQuarterCutoffDate,
      publication?.hash,
    ],
    queryFn: ({ signal }) => {
      const params: any = {
        filters: {
          review_sources: filters?.reviewSources,
          ...(selectedQuarterCutoffDate ? { end_created_at: selectedQuarterCutoffDate } : {}),
        },
        ...(publication?.hash ? { public_hash: publication.hash } : {}),
      };
      return fetchWidgetData(community?.id, WIDGET_TYPES.RATING_GRAPH, DATA_RANGE_TYPE.NOW, params, { signal });
    },
    select: (data?: any[]) => {
      // The data endpoint doesn't always give ratings in the right order of competitors.
      // therefore we need to sort it according to our community&competitor list.
      const ordering = [community, ...competitors];

      return ordering.map((entry: any) =>
        data?.find((communityRating: any) => communityRating.community_id === entry.id)
      );
    },
    enabled: allowQueries,
    refetchOnWindowFocus: false,
  });

  const {
    data: feesIncentives,
    isLoading: feesIncentivesLoading,
    isFetched: isFeesIncentivesFetched,
  } = useQuery({
    queryKey: [
      'CMASummary',
      'feesIncentive',
      community?.id,
      effectiveCompetitors,
      filters?.contractTypes,
      communitySnapshotUuid,
      snapshotsByCommunity,
      viewFilters?.dataQuarterSelection?.selectedQuarterSlug,
      viewFilters?.dataQuarterSelection?.includePriorCompetitors,
      publication?.hash,
    ],
    queryFn: ({ signal }) => {
      const communityRequest = buildSnapshotRequest(
        communitySnapshotUuid ?? null,
        (snapshot) =>
          getFeesIncentives(
            community.id,
            {
              contract_types: filters?.contractTypes,
              snapshot,
              ...(publication?.hash ? { public_hash: publication.hash } : {}),
            },
            { signal }
          ).then((response) => response?.map((item: any) => mapCommunityFeeResponseToData(item)) ?? []),
        [] as any[]
      );

      const competitorRequests = effectiveCompetitors.map((competitor) => {
        const snapshotUuid = getById(snapshotsByCommunity, competitor.id) ?? null;

        return buildSnapshotRequest(
          snapshotUuid,
          (snapshot) =>
            getFeesIncentives(
              competitor.id,
              {
                contract_types: filters?.contractTypes,
                snapshot,
                ...(publication?.hash ? { public_hash: publication.hash } : {}),
              },
              { signal }
            ).then((response) => response?.map((item: any) => mapCommunityFeeResponseToData(item)) ?? []),
          [] as any[]
        );
      });

      return Promise.all([communityRequest, ...competitorRequests]);
    },
    enabled: allowQueries,
    refetchOnWindowFocus: false,
  });

  const {
    data: fees,
    isLoading: feesLoading,
    isFetched: isFeesFetched,
  } = useQuery({
    queryKey: [
      'CMASummary',
      'fees',
      community?.id,
      effectiveCompetitors,
      communitySnapshotUuid,
      snapshotsByCommunity,
      viewFilters?.dataQuarterSelection?.selectedQuarterSlug,
      viewFilters?.dataQuarterSelection?.includePriorCompetitors,
      publication?.hash,
    ],
    queryFn: ({ signal }) => {
      const communityRequest = buildSnapshotRequest(
        communitySnapshotUuid ?? null,
        (snapshotUuid) =>
          getFees(
            community.id,
            {
              snapshot: snapshotUuid,
              ...(publication?.hash ? { public_hash: publication.hash } : {}),
            },
            { signal }
          ).then((response) => response?.map((item: any) => mapCareFeeResponseToData(item)) ?? []),
        []
      );

      const competitorRequests = effectiveCompetitors.map((competitor) =>
        buildSnapshotRequest(
          getById(snapshotsByCommunity, competitor.id) ?? null,
          (snapshotUuid) =>
            getFees(
              competitor.id,
              {
                snapshot: snapshotUuid,
                ...(publication?.hash ? { public_hash: publication.hash } : {}),
              },
              { signal }
            ).then((response) => response?.map((item: any) => mapCareFeeResponseToData(item)) ?? []),
          []
        )
      );

      return Promise.all([communityRequest, ...competitorRequests]);
    },
    enabled: allowQueries,
    refetchOnWindowFocus: false,
  });

  const {
    data: occupancies,
    isLoading: occupanciesLoading,
    isFetched: isOccupanciesFetched,
  } = useQuery({
    queryKey: [
      'CMASummary',
      'occupancies',
      community?.id,
      effectiveCompetitors,
      communitySnapshotUuid,
      snapshotsByCommunity,
      viewFilters?.dataQuarterSelection?.selectedQuarterSlug,
      viewFilters?.dataQuarterSelection?.includePriorCompetitors,
      publication?.hash,
    ],
    queryFn: ({ signal }) => {
      const communityRequest = buildSnapshotRequest(
        communitySnapshotUuid ?? null,
        (snapshotUuid) =>
          getOccupancies(
            community.id,
            {
              snapshot: snapshotUuid,
              ...(publication?.hash ? { public_hash: publication.hash } : {}),
            },
            { signal }
          ).then((response) => response?.map((occupancy: any) => mapCommunityOccupancyResponseToData(occupancy)) ?? []),
        [] as any[]
      );

      const competitorRequests = effectiveCompetitors.map((competitor) =>
        buildSnapshotRequest(
          getById(snapshotsByCommunity, competitor.id) ?? null,
          (snapshotUuid) =>
            getOccupancies(
              competitor.id,
              {
                snapshot: snapshotUuid,
                ...(publication?.hash ? { public_hash: publication.hash } : {}),
              },
              { signal }
            ).then(
              (response) => response?.map((occupancy: any) => mapCommunityOccupancyResponseToData(occupancy)) ?? []
            ),
          [] as any[]
        )
      );

      return Promise.all([communityRequest, ...competitorRequests]);
    },
    enabled: allowQueries,
    refetchOnWindowFocus: false,
  });

  const quarterSlugs = useMemo(() => quarters.map((quarter) => quarter.slug), [quarters]);
  const { startDate: incentiveStartDate, endDate: incentiveEndDate } = useMemo(
    () =>
      getIncentiveQuarterDateRange(
        dataQuarterSelection?.selectedQuarterSlug ?? null,
        quarterSlugs,
        isLatestQuarterSelected
      ),
    [dataQuarterSelection?.selectedQuarterSlug, quarterSlugs, isLatestQuarterSelected]
  );

  const {
    data: incentives,
    isLoading: incentivesLoading,
    isFetched: isIncetivesFetched,
  } = useQuery({
    queryKey: [
      'CMASummary',
      'incentives',
      community?.id,
      competitors,
      filters?.careTypes,
      viewFilters?.dataQuarterSelection?.selectedQuarterSlug,
      viewFilters?.dataQuarterSelection?.includePriorCompetitors,
      incentiveStartDate,
      incentiveEndDate,
      publication?.hash,
    ],
    queryFn: ({ signal }) => {
      const fetchAllIncentives = async (community: any, competitors: any[]) => {
        const filterParams = {
          status: 1,
          care_types: filters?.careTypes?.length ? filters?.careTypes : undefined,
          ...(incentiveStartDate ? { start_created_at: incentiveStartDate } : {}),
          ...(incentiveEndDate ? { end_created_at: incentiveEndDate } : {}),
          ...(publication?.hash ? { public_hash: publication.hash } : {}),
        };

        const requests = [getIncentives(community.id, { ...filterParams, competitors: [community.id] }, { signal })];

        competitors.forEach((competitor) => {
          requests.push(
            getIncentives(
              competitor.id,
              {
                ...filterParams,
                competitors: [competitor.id],
              },
              { signal }
            )
          );
        });

        const responses = await Promise.all(requests);
        return responses;
      };

      return fetchAllIncentives(community, competitors);
    },
    enabled: Boolean(community) && snapshotUuids !== null,
    refetchOnWindowFocus: false,
  });

  const {
    data: livingInfo,
    isLoading: livingInformationLoading,
    isFetched: isLivingInfoFetched,
  } = useQuery({
    queryKey: [
      'CMASummary',
      'livingInfo',
      community?.id,
      effectiveCompetitors,
      filters,
      communitySnapshotUuid,
      snapshotsByCommunity,
      viewFilters?.dataQuarterSelection?.selectedQuarterSlug,
      viewFilters?.dataQuarterSelection?.includePriorCompetitors,
      publication?.hash,
    ],
    queryFn: ({ signal }) => {
      const basePayload = {
        living_type: filters?.careTypes,
        apartment_type: filters?.roomTypes,
        ...(publication?.hash ? { public_hash: publication.hash } : {}),
      };

      const communityRequest = buildSnapshotRequest(
        communitySnapshotUuid ?? null,
        (snapshotUuid) =>
          getLivingInfo(
            community.id,
            {
              ...basePayload,
              snapshot: snapshotUuid,
            },
            { signal }
          ).then(
            (response) =>
              response?.map((livingInfo: any) => mapCommunityLivingInfoToData(livingInfo, community.id)) ?? []
          ),
        [] as any[]
      );

      const competitorRequests = effectiveCompetitors.map((competitor) =>
        buildSnapshotRequest(
          getById(snapshotsByCommunity, competitor.id) ?? null,
          (snapshotUuid) =>
            getLivingInfo(
              competitor.id,
              {
                ...basePayload,
                snapshot: snapshotUuid,
              },
              { signal }
            ).then(
              (response) =>
                response?.map((livingInfo: any) => mapCommunityLivingInfoToData(livingInfo, competitor.id)) ?? []
            ),
          [] as any[]
        )
      );

      return Promise.all([communityRequest, ...competitorRequests]);
    },
    enabled: allowQueries,
    refetchOnWindowFocus: false,
  });

  const {
    data: contractBenefits,
    isLoading: contractBenefitsLoading,
    isFetched: isContractBenefitsFetched,
  } = useQuery({
    queryKey: [
      'CMASummary',
      'contractBenefits',
      community?.id,
      effectiveCompetitors,
      communitySnapshotUuid,
      snapshotsByCommunity,
      viewFilters?.dataQuarterSelection?.selectedQuarterSlug,
      viewFilters?.dataQuarterSelection?.includePriorCompetitors,
      publication?.hash,
    ],
    queryFn: ({ signal }) => {
      const communityRequest = buildSnapshotRequest(
        communitySnapshotUuid ?? null,
        (snapshotUuid) =>
          getContractBenefits(
            community.id,
            {
              snapshot: snapshotUuid,
              ...(publication?.hash ? { public_hash: publication.hash } : {}),
            },
            { signal }
          ),
        [] as any[]
      );

      const competitorRequests = effectiveCompetitors.map((competitor) =>
        buildSnapshotRequest(
          getById(snapshotsByCommunity, competitor.id) ?? null,
          (snapshotUuid) =>
            getContractBenefits(
              competitor.id,
              {
                snapshot: snapshotUuid,
                ...(publication?.hash ? { public_hash: publication.hash } : {}),
              },
              { signal }
            ),
          [] as any[]
        )
      );

      return Promise.all([communityRequest, ...competitorRequests]).then((responses) =>
        responses.flatMap((r) => r ?? [])
      );
    },
    enabled: allowQueries,
    refetchOnWindowFocus: false,
  });

  const loading =
    ratingsLoading ||
    feesIncentivesLoading ||
    feesLoading ||
    livingInformationLoading ||
    occupanciesLoading ||
    incentivesLoading ||
    contractBenefitsLoading;

  const isFetched =
    isLivingInfoFetched &&
    isIncetivesFetched &&
    isOccupanciesFetched &&
    isFeesFetched &&
    isFeesIncentivesFetched &&
    isRatingsFetched &&
    isContractBenefitsFetched;

  return {
    loading,
    isFetched,
    ratings,
    feesIncentives,
    incentives,
    fees,
    occupancies,
    livingInfo,
    contractBenefits,
    snapshotMetaByCommunity,
    dataQuarterSelection,
    hasSnapshotOverrides,
  };
};

export const useFocusNewestRow = (
  roomComparisonType: number,
  roomComparisonRows: any[],
  containerRef: React.RefObject<HTMLDivElement | null>,
  numberOfRows: number,
  setNumberOfRows: (number: number) => void
) => {
  useEffect(() => {
    if (roomComparisonType === CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_OPTIONS.ADVANCED_COMPARISON) {
      if (!roomComparisonRows?.length) {
        return;
      }

      if (roomComparisonRows.length > numberOfRows) {
        // focus the newly added filter row
        if (containerRef.current) {
          const allInputFields = containerRef?.current?.querySelectorAll('input');
          if (allInputFields.length) {
            allInputFields[allInputFields.length - 1].focus();
          }
        }
      }
      setNumberOfRows(roomComparisonRows.length);
    } else {
      setNumberOfRows(0);
    }
  }, [roomComparisonRows, roomComparisonType]);
};

export const useCMASummaryData = ({
  community,
  competitors,
  filters,
}: {
  community: CommunityType;
  competitors: CommunityType[];
  filters: CMASummaryFilters;
}) => {
  const { livingTypes: livingTypes, apartmentTypeGroups, contracts } = useAppSelector((state) => state.global);
  const { competitorId } = useCommunityViewContext();

  const filteredCompetitors = competitorId
    ? competitors
    : filters?.competitors?.length
      ? competitors?.filter((competitor) => filters?.competitors.includes(competitor.id))
      : competitors;

  const {
    loading: isCMADataLoading,
    isFetched: isCMADataFetched,
    ratings,
    feesIncentives,
    incentives,
    fees,
    occupancies,
    livingInfo,
    contractBenefits,
    snapshotMetaByCommunity,
    dataQuarterSelection,
    hasSnapshotOverrides,
  } = useFetchCMASummaryData(community, filteredCompetitors || [], filters);

  const CMASummaryData = getCMASummaryData({
    community,
    competitors: filteredCompetitors,
    ratings,
    livingTypes,
    feesIncentives,
    incentives,
    fees,
    occupancies,
    livingInfo,
    apartmentTypeGroups,
    contractBenefits,
    contracts,
    filters,
    snapshotMetaByCommunity: hasSnapshotOverrides ? snapshotMetaByCommunity : undefined,
    hasSnapshotOverrides,
    dataQuarterSelection,
  });

  return { CMASummaryData, isCMADataLoading, isCMADataFetched };
};
